import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'safesourcer_access_token';
const REFRESH_KEY = 'safesourcer_refresh_token';

// Create an axios instance with auth interceptor
const api = axios.create({ baseURL: BACKEND_URL });

// Session-expired event bus
const sessionExpiredCallbacks = [];
function onSessionExpired(cb) { sessionExpiredCallbacks.push(cb); }
function emitSessionExpired() { sessionExpiredCallbacks.forEach(cb => cb()); }

api.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor: auto-detect 401 and trigger session expired
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (
      error.response?.status === 401 &&
      !error.config?.url?.includes('/auth/login') &&
      !error.config?.url?.includes('/auth/register')
    ) {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_KEY);
      emitSessionExpired();
    }
    return Promise.reject(error);
  }
);

function getErrorMessage(error) {
  if (error.response) {
    const detail = error.response.data?.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      return detail
        .map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e)))
        .filter(Boolean)
        .join(' ');
    }
    if (detail && typeof detail.msg === 'string') return detail.msg;
    if (detail) return String(detail);
    return `Server error (${error.response.status})`;
  }
  if (error.request) {
    if (error.message && error.message.includes('Network Error')) {
      return 'Network error: Unable to reach the server. Please check your connection.';
    }
    if (error.code === 'ECONNABORTED') {
      return 'Request timed out. Please try again.';
    }
    return 'No response from server. Please try again later.';
  }
  return error.message || 'An unexpected error occurred.';
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);

  // Listen for session-expired events from interceptor
  useEffect(() => {
    onSessionExpired(() => {
      setUser(false);
      setSessionExpired(true);
    });
  }, []);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get('/api/auth/me');
      setUser(data);
    } catch (error) {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_KEY);
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await api.post('/api/auth/login', { email, password });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      if (data.refresh_token) localStorage.setItem(REFRESH_KEY, data.refresh_token);
      setUser({ id: data.id, email: data.email, name: data.name, role: data.role });
      setSessionExpired(false);
      return { success: true };
    } catch (error) {
      return { success: false, error: getErrorMessage(error) };
    }
  }, []);

  const register = useCallback(async (email, password, name) => {
    try {
      const { data } = await api.post('/api/auth/register', { email, password, name });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      if (data.refresh_token) localStorage.setItem(REFRESH_KEY, data.refresh_token);
      setUser({ id: data.id, email: data.email, name: data.name, role: data.role });
      setSessionExpired(false);
      return { success: true };
    } catch (error) {
      return { success: false, error: getErrorMessage(error) };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/api/auth/logout');
    } finally {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_KEY);
      setUser(false);
      setSessionExpired(false);
    }
  }, []);

  const dismissSessionExpired = useCallback(() => setSessionExpired(false), []);

  const contextValue = React.useMemo(
    () => ({ user, loading, login, register, logout, checkAuth, sessionExpired, dismissSessionExpired }),
    [user, loading, sessionExpired, login, register, logout, checkAuth, dismissSessionExpired]
  );

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export { api };
